import * as React from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderClosed,
  Scissors,
  Download,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Cpu,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export function AppShell() {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const user = useAuthStore((state) => state.user);
  const tenant = useAuthStore((state) => state.tenant);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Projects', path: '/projects', icon: FolderClosed },
    { label: 'Optimisation', path: '/optimisation/runs', icon: Cpu },
    { label: 'Off-cuts', path: '/offcuts', icon: Scissors },
    { label: 'Exports', path: '/exports', icon: Download },
    { label: 'Settings', path: '/settings', icon: Settings },
  ];

  // Auto-close mobile drawer on route change
  React.useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const userInitials = React.useMemo(() => {
    if (!user) return 'U';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  }, [user]);

  const userFullName = React.useMemo(() => {
    if (!user) return 'User';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
  }, [user]);

  return (
    <div className="flex h-dvh overflow-hidden bg-bg-secondary text-fg-primary">
      {/* Sidebar for Desktop */}
      <aside
        className={`hidden md:flex flex-col border-r border-border bg-bg-elevated transition-all duration-300 ${
          isSidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Brand header */}
        <div className="flex h-16 items-center justify-between px-6 border-b border-border">
          <div className={`flex items-center gap-3 transition-opacity ${!isSidebarOpen && 'opacity-0 w-0 overflow-hidden'}`}>
            <span className="text-xl font-bold tracking-tight text-primary">Cutlyft</span>
            <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              v1.0
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-fg-secondary hover:text-fg-primary"
          >
            {isSidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
          </Button>
        </div>

        {/* Navigation list */}
        <nav className="flex-1 space-y-1.5 p-4 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-fg-secondary hover:bg-secondary hover:text-fg-primary'
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className={`transition-opacity duration-200 ${!isSidebarOpen && 'opacity-0 w-0 overflow-hidden'}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border flex flex-col items-center">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 text-error hover:bg-error-bg hover:text-error ${
              !isSidebarOpen && 'justify-center px-0'
            }`}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span className={`${!isSidebarOpen && 'hidden'}`}>Log Out</span>
          </Button>
        </div>
      </aside>

      {/* Mobile Drawer Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden animate-in fade-in"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar / Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-bg-elevated border-r border-border transition-transform duration-300 md:hidden ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-6 border-b border-border">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold tracking-tight text-primary">Cutlyft</span>
            <span className="rounded bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              v1.0
            </span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(false)}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 space-y-1.5 p-4 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-fg-secondary hover:bg-secondary hover:text-fg-primary'
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 text-error hover:bg-error-bg hover:text-error"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <span>Log Out</span>
          </Button>
        </div>
      </aside>

      {/* Main Content Pane */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top Header */}
        <header className="flex h-16 items-center justify-between border-b border-border bg-bg-elevated px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileOpen(true)}
              className="md:hidden text-fg-secondary hover:text-fg-primary"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h2 className="hidden md:block text-sm font-semibold text-fg-secondary">
              {tenant?.name || 'Fabricator Owner'}
            </h2>
          </div>

          <div className="flex items-center gap-4">
            {/* User Dropdown Profile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full focus-visible:ring-offset-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src="" alt={userFullName} />
                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-sm">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-semibold leading-none text-fg-primary">
                      {userFullName}
                    </p>
                    <p className="text-xs leading-none text-fg-muted">
                      {user?.email || ''}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings?tab=profile')}>
                  <User className="mr-2 h-4 w-4" />
                  <span>My Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-error focus:bg-error-bg focus:text-error">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Outlet area for child routes */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
export default AppShell;
